import { useCallback } from "react";
import { useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { usePopoverPaginationInfiniteScroll } from "@skydio/relay-toolkit";
import { ComboBox, useDebouncedState } from "@skydio/rivet-ui";

import { useFilteredItemWithMemory } from "./useFilteredItemWithMemory";

import type { Key } from "@skydio/rivet-ui";
import type { InputVariantProps } from "@skydio/rivet-ui/src/components/input/useInput";
import type { OptionalSingleSelectProps } from "@skydio/rivet-ui/src/hooks/useMultiSelectListState";
import type { OrgNameUuidFilter_query$key } from "__generated__/OrgNameUuidFilter_query.graphql";
import type { OrgNameUuidFilterQuery } from "__generated__/OrgNameUuidFilterQuery.graphql";
import type { OrgNameUuidFilterRefetchQuery } from "__generated__/OrgNameUuidFilterRefetchQuery.graphql";

const RECENT_ORGS_KEY = "org_name_uuid_filter_recent_orgs";
const MAX_RECENT_ORGS = 5;

interface RecentOrg {
  uuid: string;
  name: string;
  vehicles?: { totalCount: number } | null;
  docks?: { totalCount: number } | null;
}

const getRecentOrgs = (): RecentOrg[] => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_ORGS_KEY) || "[]");
  } catch {
    return [];
  }
};

const addRecentOrg = (org: RecentOrg) => {
  const recentOrgs = getRecentOrgs().filter(o => o.uuid !== org.uuid);
  recentOrgs.unshift(org);
  if (recentOrgs.length > MAX_RECENT_ORGS) {
    recentOrgs.pop();
  }
  localStorage.setItem(RECENT_ORGS_KEY, JSON.stringify(recentOrgs));
};

const orgNameUuidFragment = graphql`
  fragment OrgNameUuidFilter_query on Query
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
    searchTerm: { type: "String" }
  )
  @refetchable(queryName: "OrgNameUuidFilterRefetchQuery") {
    organizations(after: $cursor, first: $count, searchTerm: $searchTerm)
      @connection(key: "OrgNameUuidFilter_organizations") {
      totalCount
      edges {
        node {
          id
          uuid
          name
          vehicles {
            totalCount
          }
          docks {
            totalCount
          }
        }
      }
    }
  }
`;

const orgNameUuidFilterQuery = graphql`
  query OrgNameUuidFilterQuery {
    ...OrgNameUuidFilter_query
  }
`;

export interface OrgNameUuidSelectFilterProps extends OptionalSingleSelectProps {
  placeholder?: string;
  /** When true, shows vehicle and dock counts instead of UUID in the description */
  showCounts?: boolean;
  /** When true, shows recently selected organizations when input is empty */
  showRecent?: boolean;
  /** Size of the input component */
  size?: InputVariantProps["size"];
}

const OrgNameUuidSelectFilter = (props: OrgNameUuidSelectFilterProps) => {
  const { onSingleSelectionChange, showRecent = false } = props;
  const [inputValue, setSearchTerm, { debouncedValue: searchTerm }] = useDebouncedState("");
  const data = useLazyLoadQuery<OrgNameUuidFilterQuery>(orgNameUuidFilterQuery, {});

  const paginationQuery = usePaginationFragment<
    OrgNameUuidFilterRefetchQuery,
    OrgNameUuidFilter_query$key
  >(orgNameUuidFragment, data);

  const [infiniteScrollProps, currentItems] = usePopoverPaginationInfiniteScroll(
    paginationQuery,
    data => data.organizations,
    10,
    { searchTerm }
  );

  const itemProps = useFilteredItemWithMemory({
    currentItems,
    selectedKey: props.selectedKey ?? null,
    getKey: organization => organization!.uuid,
  });

  const handleSelectionChange = useCallback(
    (key: Key | null) => {
      if (key && onSingleSelectionChange) {
        const selectedOrg = currentItems.find(org => org?.uuid === key);
        if (selectedOrg && showRecent) {
          addRecentOrg({
            uuid: selectedOrg.uuid,
            name: selectedOrg.name,
            vehicles: selectedOrg.vehicles,
            docks: selectedOrg.docks,
          });
        }
        onSingleSelectionChange(key);
      }
    },
    [currentItems, onSingleSelectionChange, showRecent]
  );

  const displayItems = inputValue || !showRecent ? currentItems : getRecentOrgs();

  return (
    <ComboBox
      placeholder={props.placeholder ?? "Org Name/ID"}
      size={props.size}
      inputValue={inputValue}
      onInputChange={setSearchTerm}
      selectedKey={props.selectedKey}
      onSingleSelectionChange={handleSelectionChange}
      aria-label={props.placeholder ?? "Org Name/ID"}
      {...(inputValue || !showRecent ? itemProps : { items: displayItems })}
      {...(inputValue || !showRecent ? infiniteScrollProps : {})}
    >
      {organization => {
        // We know organization will never be null in this context
        const org = organization!;
        return (
          <ComboBox.Item
            key={org.uuid}
            description={
              props.showCounts
                ? `${org.vehicles?.totalCount ?? 0} vehicles, ${org.docks?.totalCount ?? 0} docks`
                : org.uuid
            }
            aria-label={org.name}
          >
            {org.name}
            {!inputValue && showRecent && <span className="ml-2 text-gray-500">(Recent)</span>}
          </ComboBox.Item>
        );
      }}
    </ComboBox>
  );
};

export default OrgNameUuidSelectFilter;
